import { motion } from "framer-motion";
import { Mail, MessageCircle, Phone } from "lucide-react";
import { SHOP } from "@/data/menu";
import { LogoMark } from "@/components/Logo";

const STEPS = [
  ["01", "Pick your treats", "Browse all 14 collections and add any flavors to your box. Mix as many as you like.", "#F472B6"],
  ["02", "Send your order", "Add your name and number, then send your order to us by text or email in one tap.", "#F59E0B"],
  ["03", "Get your quote", "We reply with your pricing and confirm the details for your pickup or delivery.", "#34D399"],
];

export const HowItWorks = () => (
  <section id="how" className="bg-cacao py-24 text-[#FAF6F0] lg:py-36" data-testid="how-section">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <h2 className="f-display max-w-3xl text-5xl font-semibold leading-[0.95] sm:text-6xl">
        Ordering is <span className="italic text-[#F472B6]">sweet</span> &amp; simple.
      </h2>
      <div className="mt-16 grid gap-10 md:grid-cols-3 md:gap-8">
        {STEPS.map(([n, t, d, col], i) => (
          <motion.div
            key={n}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, delay: i * 0.12 }}
            className="border-t border-white/15 pt-8"
            data-testid={`how-step-${n}`}
          >
            <span className="f-display text-7xl font-semibold italic" style={{ color: col }}>{n}</span>
            <h3 className="f-display mt-4 text-3xl font-semibold">{t}</h3>
            <p className="mt-3 max-w-sm text-base leading-relaxed text-[#E8DCD6]">{d}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const Contact = () => (
  <section id="contact" className="grain relative overflow-hidden bg-blush py-24 lg:py-32" data-testid="contact-section">
    <div className="relative mx-auto grid max-w-[1400px] gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:items-center">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.8 }}
        className="lg:col-span-7"
      >
        <h2 className="f-display text-5xl font-semibold leading-[0.95] text-cacao sm:text-7xl">
          Planning a party? <span className="block italic text-rose">Let's make it sweet.</span>
        </h2>
        <p className="mt-6 max-w-lg text-base text-cacao-2">
          Birthdays, baby showers, weddings and holidays. Ask us about custom colors, names and themed designs.
        </p>
      </motion.div>
      <div className="flex flex-col gap-3 lg:col-span-5">
        {[
          [`sms:+1${SHOP.phone}`, MessageCircle, "Text us", SHOP.phoneDisplay, "contact-text-link"],
          [`tel:+1${SHOP.phone}`, Phone, "Call us", SHOP.phoneDisplay, "contact-call-link"],
          [`mailto:${SHOP.email}`, Mail, "Email us", SHOP.email, "contact-email-link"],
        ].map(([href, Icon, label, val, tid]) => (
          <a
            key={tid}
            href={href}
            data-testid={tid}
            className="group flex items-center justify-between gap-4 rounded-2xl border border-[#2D1E18]/10 bg-white/70 px-6 py-5 backdrop-blur transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-white"
          >
            <span className="flex items-center gap-4">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-[#E11D48] text-white"><Icon className="h-5 w-5" /></span>
              <span>
                <span className="f-mono block text-xs uppercase tracking-[0.2em] text-cacao-2">{label}</span>
                <span className="block break-all font-medium text-cacao">{val}</span>
              </span>
            </span>
          </a>
        ))}
      </div>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="bg-cacao py-12 text-[#E8DCD6]" data-testid="site-footer">
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-5 sm:flex-row sm:items-center sm:justify-between sm:px-8">
      <div className="flex items-center gap-3">
        <LogoMark className="h-10 w-10" />
        <span className="f-display text-2xl italic text-white">{SHOP.name}</span>
      </div>
      <p className="text-sm">© {new Date().getFullYear()} {SHOP.name}. Handmade with love.</p>
    </div>
  </footer>
);
